import React from 'react';

import { FileText, PenTool, CreditCard, CheckCircle, ArrowRight, ArrowLeft, Loader2, Phone, Mail, AlertTriangle, Download, Shield, Eye, RefreshCw, ScrollText, Stamp, Heart, Clock, Sparkles, PartyPopper, Save, BookmarkPlus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';

import { toast } from 'sonner';

import SignatureFTPEBlock, { FTPEStamp } from '../../components/SignatureFTPEBlock';
import { DocumentCardFTPE } from '../../components/DocumentViewerFTPE';

import FTPEPaymentOptions from '../../components/FTPEPaymentOptions';
import PushNotificationPrompt from '../../components/PushNotificationPrompt';

import { FTPE_DOCUMENTS } from './ftpeConstants';

export default function FtpeSteps456({ ctx }) {
  const { adhesionId, currentStep, debugCode, formData, handleDownloadBulletin, handleDownloadDocument, handleSaveDraft, handleSendOTP, handleValidateSignature, lastSaved, lieuSignature, loading, navigate, otpCode, otpLoading, otpSent, paymentStatus, savingDraft, setCurrentStep, setLieuSignature, setLoading, setOtpCode, setOtpSent, setPaymentStatus, text: _text, url: url, user } = ctx;
  return (
    <>
        {/* ÉTAPE 4: Signature SMS */}
        {currentStep === 4 && (
          <Card className="border-0 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-emerald-50 border-b">
              <CardTitle className="flex items-center gap-2">
                <PenTool className="w-5 h-5 text-amber-600" />
                Signature électronique
              </CardTitle>
              <CardDescription>
                Consultez les documents officiels puis validez votre signature par SMS
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              
              {/* Section Documents Officiels FTPE - Design Premium avec filigrane */}
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
                      <ScrollText className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-bold text-slate-800">Documents officiels à consulter</h3>
                  </div>
                  <Badge
                    className="bg-slate-100 text-slate-700 border border-slate-300 text-[10px] font-semibold"
                    data-testid="ftpe-docs-download-unavailable-badge"
                  >
                    TÉLÉCHARGEMENT INDISPONIBLE
                  </Badge>
                </div>

                <p className="text-xs text-slate-500 italic px-1">
                  Ces documents officiels (Statuts, Règlement Intérieur, Charte Éthique) sont disponibles uniquement en consultation.
                </p>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Statuts FTPE */}
                  <DocumentCardFTPE
                    url={FTPE_DOCUMENTS.statuts}
                    title="Statuts"
                    subtitle="FTPE Outremer 2025"
                    description="Constitution, objet, siège social, composition et fonctionnement de la fédération."
                    icon={Shield}
                    color="amber"
                    showWatermark={true}
                    onDownload={() => handleDownloadDocument('statuts')}
                  />

                  {/* Règlement Intérieur */}
                  <DocumentCardFTPE
                    url={FTPE_DOCUMENTS.reglement}
                    title="Règlement Intérieur"
                    subtitle="Juin 2025"
                    description="Droits et devoirs des adhérents, modalités de fonctionnement et sanctions."
                    icon={ScrollText}
                    color="emerald"
                    showWatermark={true}
                    onDownload={() => handleDownloadDocument('reglement')}
                  />

                  {/* Charte Éthique - NOUVEAU */}
                  <DocumentCardFTPE
                    url={FTPE_DOCUMENTS.charte}
                    title="Charte Éthique"
                    subtitle="FTPE Outremer"
                    description="Engagements éthiques et valeurs de la FTPE Outremer pour ses adhérents."
                    icon={Heart}
                    color="purple"
                    showWatermark={true}
                    onDownload={() => handleDownloadDocument('charte')}
                  />

                  {/* Bulletin d'adhésion */}
                  <div className="group relative overflow-hidden rounded-xl border-2 border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-50 hover:border-amber-400 hover:shadow-lg transition-all duration-300">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-slate-300/20 to-transparent" />
                    <div className="absolute top-2 right-2">
                      <Badge className="bg-amber-500 text-white border-0 text-[10px] font-semibold">
                        À SIGNER
                      </Badge>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                          <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-slate-900">Bulletin d&apos;adhésion</p>
                          <p className="text-xs text-slate-500">Votre dossier</p>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 mb-4 line-clamp-2">
                        Récapitulatif de votre demande d&apos;adhésion avec vos informations.
                      </p>
                      <Button 
                        onClick={handleDownloadBulletin}
                        className="flex items-center justify-center gap-2 w-full py-2.5 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg"
                      >
                        <Eye className="w-4 h-4" />
                        Voir mon bulletin
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Note importante */}
                <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-amber-50 to-emerald-50 rounded-xl border border-amber-200">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-800 text-sm">Important</p>
                    <p className="text-xs text-amber-700">
                      En signant ce bulletin, vous attestez avoir pris connaissance des Statuts, du Règlement Intérieur et de la Charte Éthique de la FTPE Outremer et vous engagez à les respecter.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bloc de signature avec tampon FTPE et QR Code */}
              <div className="border-2 border-emerald-100 rounded-xl p-6 bg-gradient-to-br from-white to-emerald-50">
                <div className="flex items-center gap-2 mb-4">
                  <Stamp className="w-5 h-5 text-emerald-600" />
                  <h4 className="font-semibold text-emerald-800">Tampon et authentification</h4>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Tampon FTPE */}
                  <div className="flex flex-col items-center p-4 bg-white rounded-lg border">
                    <p className="text-sm text-slate-600 mb-3">Tampon FTPE Outremer</p>
                    <FTPEStamp 
                      size="large"
                      dateSignature={otpSent && otpCode ? new Date().toISOString() : null}
                      lieuSignature={lieuSignature || null}
                      nomSignataire={formData.representant_nom || null}
                      showDetails={otpSent}
                    />
                  </div>
                  
                  {/* Prévisualisation QR Code */}
                  <div className="flex flex-col items-center p-4 bg-white rounded-lg border">
                    <p className="text-sm text-slate-600 mb-3">QR Code de vérification</p>
                    {adhesionId ? (
                      <SignatureFTPEBlock
                        adhesion={{ id: adhesionId, representant_nom: formData.representant_nom }}
                        compact={true}
                        showQRCode={true}
                      />
                    ) : (
                      <div className="w-24 h-24 bg-slate-100 rounded-lg flex items-center justify-center">
                        <p className="text-xs text-slate-400 text-center px-2">
                          QR généré après validation
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Bouton de sauvegarde pour reprise ultérieure */}
              <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <BookmarkPlus className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-800">Sauvegarder pour plus tard ?</p>
                    <p className="text-xs text-blue-600">
                      Vous pourrez reprendre votre adhésion pendant 30 jours
                      {lastSaved && (
                        <span className="ml-2">• Dernière sauvegarde : {lastSaved.toLocaleTimeString()}</span>
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleSaveDraft}
                  disabled={savingDraft}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  {savingDraft ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Sauvegarder
                </Button>
              </div>

              {/* Envoi du SMS */}
              {!otpSent ? (
                <div className="text-center py-8">
                  <Phone className="w-16 h-16 mx-auto text-amber-500 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    Validation par SMS
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Un code de validation sera envoyé au numéro :<br />
                    <strong>{formData.telephone}</strong>
                  </p>
                  <div className="flex justify-center gap-4">
                    <Button 
                      variant="outline"
                      onClick={() => setCurrentStep(3)}
                      className="h-12"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Modifier les infos
                    </Button>
                    <Button 
                      onClick={handleSendOTP}
                      disabled={otpLoading}
                      className="bg-amber-600 hover:bg-amber-700 h-12"
                    >
                      {otpLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Phone className="w-4 h-4 mr-2" />
                      )}
                      Envoyer le code SMS
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Code reçu en debug */}
                  {debugCode && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
                      <p className="text-sm text-blue-700">
                        Mode simulation - Code : <strong className="text-lg">{debugCode}</strong>
                      </p>
                    </div>
                  )}
                  
                  <div>
                    <Label>Code SMS reçu *</Label>
                    <Input 
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      placeholder="000000"
                      className="text-center text-2xl tracking-widest"
                      maxLength={6}
                    />
                  </div>
                  
                  <div>
                    <Label>Lieu de signature *</Label>
                    <Input 
                      value={lieuSignature}
                      onChange={(e) => setLieuSignature(e.target.value)}
                      placeholder="Ex: Basse-Terre"
                    />
                  </div>
                  
                  <div className="p-4 bg-amber-50 rounded-lg text-sm">
                    <p className="font-medium text-amber-800 mb-2">
                      En validant, je confirme avoir lu et accepté :
                    </p>
                    <ul className="list-disc list-inside text-amber-700 space-y-1">
                      <li>Les statuts de la FTPE Outremer</li>
                      <li>Le règlement intérieur</li>
                      <li>La charte d&apos;éthique</li>
                    </ul>
                  </div>
                  
                  <div className="flex gap-4">
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setOtpSent(false);
                        setOtpCode('');
                        setCurrentStep(3);
                      }}
                      className="h-12"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Retour
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={handleSendOTP}
                      disabled={otpLoading}
                      className="h-12"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Renvoyer le code
                    </Button>
                    <Button 
                      onClick={handleValidateSignature}
                      disabled={loading || !otpCode || !lieuSignature}
                      className="flex-1 bg-black hover:bg-gray-800 h-12"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Valider la signature
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ÉTAPE 5: Paiement */}
        {currentStep === 5 && (
          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-amber-600" />
                Paiement de la cotisation
              </CardTitle>
              <CardDescription>
                Choisissez votre mode de paiement préféré
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Prompt notification push */}
              <PushNotificationPrompt />
              
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-green-700 font-medium text-center">Document signé électroniquement</p>
              </div>
              
              <FTPEPaymentOptions
                adhesionId={adhesionId}
                amount={200}
                loading={loading}
                setLoading={setLoading}
                onPaymentSuccess={() => {
                  setPaymentStatus('completed');
                  setCurrentStep(6);
                }}
                onPaymentPending={() => {
                  setPaymentStatus('pending_virement');
                  setCurrentStep(6);
                }}
              />
              
              {/* Option payer plus tard */}
              <div className="border-t pt-6 mt-6">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-700">Vous souhaitez payer plus tard ?</p>
                    <p className="text-sm text-gray-500">Votre demande restera accessible pendant 30 jours</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      toast.success('Votre adhésion a été sauvegardée. Vous pourrez la reprendre depuis votre espace.');
                      navigate('/');
                    }}
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Payer plus tard
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ÉTAPE 6: Confirmation */}
        {currentStep === 6 && (
          <Card className="border-0 shadow-xl overflow-hidden">
            <CardContent className="p-8 text-center">
              {paymentStatus === 'completed' ? (
                <>
                  {/* Animation de succès */}
                  <div className="relative">
                    <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg animate-bounce">
                      <CheckCircle className="w-12 h-12 text-white" />
                    </div>
                    <Sparkles className="absolute top-0 right-1/3 w-6 h-6 text-amber-400 animate-pulse" />
                    <PartyPopper className="absolute top-2 left-1/3 w-5 h-5 text-pink-500 animate-pulse" />
                  </div>
                  
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    Paiement réussi !
                  </h2>
                  <p className="text-xl text-emerald-600 font-semibold mb-4">
                    Votre adhésion FTPE est maintenant active
                  </p>
                  
                  {/* Badge de confirmation */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full mb-6">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">Adhérent FTPE Outremer</span>
                  </div>
                  
                  {/* Bloc de signature avec tampon, horodatage et QR code */}
                  <div className="max-w-2xl mx-auto mb-8">
                    <SignatureFTPEBlock
                      adhesion={{
                        id: adhesionId,
                        numero_adhesion: adhesionId,
                        representant_nom: formData.representant_nom,
                        date_signature: new Date().toISOString(),
                        lieu_signature: lieuSignature
                      }}
                      signatureData={{
                        date: new Date().toISOString(),
                        lieu: lieuSignature,
                        nom: formData.representant_nom
                      }}
                      isSignable={false}
                      showQRCode={true}
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4 max-w-lg mx-auto mb-8">
                    <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200">
                      <FileText className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                      <p className="font-medium text-gray-900">Attestation d&apos;adhésion</p>
                      <p className="text-sm text-amber-600">Disponible au téléchargement</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                      <Mail className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <p className="font-medium text-gray-900">Reçu de paiement</p>
                      <p className="text-sm text-blue-600">Envoyé par email</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl border border-amber-300 mb-8">
                    <p className="text-amber-800 font-semibold text-lg">
                      🎉 Bienvenue à la FTPE Outremer !
                    </p>
                    <p className="text-amber-700 text-sm mt-1">
                      Vous faites maintenant partie de la fédération des TPE ultramarines
                    </p>
                  </div>
                  
                  <div className="flex gap-4 justify-center flex-wrap">
                    <Button variant="outline" onClick={handleDownloadBulletin} className="border-amber-300 hover:bg-amber-50">
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger l&apos;attestation
                    </Button>
                    <Button onClick={() => {
                      const dashboardRoutes = {
                        'super_admin': '/admin/super-dashboard',
                        'admin': '/admin/dashboard',
                        'cooper': '/cooper/dashboard',
                        'member': '/member/dashboard',
                        'expert': '/expert/dashboard'
                      };
                      navigate(dashboardRoutes[user?.role] || '/');
                    }} className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg">
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Accéder à mon espace
                    </Button>
                  </div>
                </>
              ) : paymentStatus === 'pending_virement' ? (
                <>
                  <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Clock className="w-10 h-10 text-amber-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Virement en attente
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Votre adhésion sera activée dès réception du virement.
                  </p>
                  
                  <div className="max-w-md mx-auto p-6 bg-amber-50 border border-amber-200 rounded-xl mb-8">
                    <h3 className="font-semibold text-amber-800 mb-4">Référence de virement</h3>
                    <div className="p-4 bg-white rounded-lg border border-amber-300 text-center">
                      <code className="font-mono text-xl font-bold text-amber-700">
                        FTPE-{adhesionId?.slice(-8).toUpperCase()}
                      </code>
                    </div>
                    <p className="text-sm text-amber-700 mt-4">
                      N&apos;oubliez pas d&apos;indiquer cette référence dans le libellé de votre virement.
                    </p>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg mb-6">
                    <p className="text-blue-800">
                      📧 Un email avec les coordonnées bancaires vous a été envoyé.
                    </p>
                  </div>
                  
                  <Button onClick={() => {
                    const dashboardRoutes = {
                      'super_admin': '/admin/super-dashboard',
                      'admin': '/admin/dashboard',
                      'cooper': '/cooper/dashboard',
                      'member': '/member/dashboard',
                      'expert': '/expert/dashboard'
                    };
                    navigate(dashboardRoutes[user?.role] || '/');
                  }} className="bg-black hover:bg-gray-800">
                    Retour à mon espace
                  </Button>
                </>
              ) : (
                <div className="py-8">
                  <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Loader2 className="w-10 h-10 animate-spin text-amber-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    Paiement en cours de validation
                  </h2>
                  <p className="text-gray-600 mb-4">
                    Nous vérifions votre paiement Stripe...
                  </p>
                  <p className="text-sm text-gray-500">
                    Cette opération ne prend que quelques secondes.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
    </>
  );
}
